import * as Form from '@radix-ui/react-form';
import { Button } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/button';
import { FormInput } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/form-input';
import { FormDatePicker } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/form-date-picker';
import { PhoneNumberInput } from '@trashlab/sitesapp/src/organizations/redebins/components/shared/phone-number-input';
import { Flex, Box, Text, Select, Checkbox } from '@radix-ui/themes';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/redebins/contexts/use-tenant-context';
import { trpcWebsiteSubmitServiceRequest } from '@trashlab/api/src/generated/hooks';
import { submitServiceRequestSchema } from '@trashlab/api/src/domains/website/validations';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { useFilteredSkus } from '@trashlab/sitesapp/src/organizations/redebins/hooks/use-filtered-skus';
import { DateTime } from 'luxon';
import { E164Number } from 'libphonenumber-js';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/redebins/lib/utils/get-variant-value';

type FormData = Omit<
  z.infer<typeof submitServiceRequestSchema>,
  'tenantId' | 'websiteId' | 'domainId'
>;

export const FormFields = ({
  serviceTypeOverride,
}: {
  /** If provided, pre-selects this service type in the form */
  serviceTypeOverride?: string;
} = {}) => {
  const { website } = useTenantContext();
  const submitServiceRequestMutation =
    trpcWebsiteSubmitServiceRequest.useMutation();

  const { containerTypeOptions, isLoading } = useFilteredSkus(
    website?.organization.id ?? '',
  );

  const submitButtonText = useVariantValue('FORM_SUBMIT_BUTTON_TEXT', 'Send');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
    setError,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(
      submitServiceRequestSchema.omit({
        tenantId: true,
        websiteId: true,
        domainId: true,
      }),
    ),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '' as E164Number,
      serviceRequested: serviceTypeOverride ?? '',
      preferredServiceDate: undefined,
    },
  });

  const preferredServiceDate = watch('preferredServiceDate');

  const onSubmit = async (data: FormData) => {
    if (!website?.organization.id) {
      setError('root', {
        message: 'Organization ID not found. Please try again later.',
      });
      return;
    }

    try {
      await submitServiceRequestMutation.mutateAsync({
        ...data,
        tenantId: website.organization.id,
        websiteId: website.id,
        domainId: website.domain.id,
      });

      reset();
      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setError('root', {
        message: errorMessage,
      });
      return false;
    }
  };

  const [isSuccess, setIsSuccess] = useState(false);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);

  // Organization name for SMS consent message
  const orgName = website?.organization.name ?? 'our company';

  if (isSuccess) {
    return (
      <Flex direction="column" gap="4" align="center" className="py-8">
        <Text size="5" weight="bold" className="text-green-600">
          Service request sent!
        </Text>
        <Text size="3">
          We have received your inquiry and will get back to you as soon as
          possible.
        </Text>
        <Button variant="soft" onClick={() => {
          setIsSuccess(false);
          setSmsConsentChecked(false);
        }}>
          Set up another service request
        </Button>
      </Flex>
    );
  }

  return (
    <Form.Root
      className="flex flex-col w-full text-sm leading-loose text-neutral-400 relative"
      onSubmit={handleSubmit(async (data) => {
        const success = await onSubmit(data);
        if (success) setIsSuccess(true);
      })}
    >
      <Flex gap="2" direction="column" className="relative">
        <Flex gap="2" direction="row" justify="between">
          <FormInput
            {...register('firstName')}
            name="firstName"
            label="First name"
            placeholder="First name"
            required
            containerClassName="flex-1"
            error={errors.firstName?.message}
          />
          <FormInput
            {...register('lastName')}
            name="lastName"
            label="Last name"
            placeholder="Last name"
            required
            containerClassName="flex-1"
            error={errors.lastName?.message}
          />
        </Flex>
        <FormInput
          {...register('email')}
          name="email"
          label="Email"
          type="email"
          placeholder="Email"
          required
          containerClassName="flex-1"
          error={errors.email?.message}
        />
        <PhoneNumberInput
          name="phone"
          label="Phone number"
          required
          containerClassName="flex-1"
          error={errors.phone?.message}
          value={watch('phone') ?? ''}
          onChange={(value) => setValue('phone', value as E164Number, { shouldValidate: true })}
        />

        <Form.Field name="serviceRequested">
          <Form.Label>Service requested</Form.Label>
          {serviceTypeOverride ? (
            // When service type is pre-set (e.g., Mobile Storage page), show as disabled input
            <Box className="w-full h-[46px] bg-gray-100 rounded-md flex items-center px-3 text-gray-700">
              {serviceTypeOverride}
            </Box>
          ) : (
            <Select.Root
              disabled={isLoading}
              onValueChange={(value) => setValue('serviceRequested', value)}
              {...register('serviceRequested')}
            >
              <Select.Trigger
                className="w-full h-[46px] bg-white rounded-md pointer-events-auto relative"
                placeholder="Select a service type"
              />
              <Select.Content
                className="w-full min-w-[var(--radix-select-trigger-width)]"
                position="popper"
                sideOffset={4}
              >
                <Select.Group>
                  <Select.Label>Service Types</Select.Label>
                  {containerTypeOptions.map((option) => (
                    <Select.Item key={option.value} value={option.label}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          )}

          {errors.serviceRequested && (
            <Form.Message className="text-red-500 text-sm" match="valueMissing">
              Please select a service type.
            </Form.Message>
          )}
        </Form.Field>

        <FormDatePicker
          name="preferredServiceDate"
          label="Service start date"
          value={preferredServiceDate}
          onChange={(date) => setValue('preferredServiceDate', date)}
          error={errors.preferredServiceDate?.message}
          minDate={DateTime.now()}
        />

        {errors.root && (
          <Text size="2" className="text-red-500 mt-2">
            {errors.root.message}
          </Text>
        )}

        {/* SMS/Call Consent Checkbox - required to submit the form */}
        <label className="flex items-start gap-3 cursor-pointer mt-4">
          <Checkbox
            checked={smsConsentChecked}
            onCheckedChange={(checked) => setSmsConsentChecked(checked === true)}
            className="mt-1 flex-shrink-0"
          />
          <Text size="2" className="text-neutral-600 leading-relaxed">
            By checking this box, I agree to receive text messages and/or calls
            from {orgName} regarding service reminders, order confirmations, and
            important account updates. Message frequency may vary. Message and
            data rates may apply. Consent is not a condition of purchase. Reply
            STOP to cancel, HELP for help.
          </Text>
        </label>

        <Form.Submit asChild>
          <Box className="flex col-span-1 mt-4">
            <Button
              withArrow
              disabled={isSubmitting || !smsConsentChecked}
              loading={isSubmitting}
              data-variable="FORM_SUBMIT_BUTTON_TEXT"
            >
              {submitButtonText}
            </Button>
          </Box>
        </Form.Submit>
      </Flex>
    </Form.Root>
  );
};

export default FormFields;
